import {
    useMutation,
    useQueryClient,
    UseMutationResult,
} from '@tanstack/react-query'
import { deleteAttribute } from '../../../../services/dtServices'
import { IAttribute } from '../../interfaces'

export const useDeleteAttribute = (
    orgId: string,
    entityId: string,
): UseMutationResult<
    Awaited<ReturnType<typeof deleteAttribute>>,
    Error,
    string,
    { previousAttributes?: IAttribute[] }
> => {
    const queryClient = useQueryClient()
    const queryKey = ['attributes', entityId, orgId]
    return useMutation({
        mutationFn: (attributeId: string) => {
            return deleteAttribute(orgId, entityId, attributeId)
        },
        /**
         * Cancel in-flight fetches before the optimistic remove so a slow GET
         * that started earlier can't resolve afterwards and resurrect the
         * deleted row (UBQ-5435). Snapshot the list so onError can roll back.
         */
        onMutate: async (attributeId: string) => {
            await queryClient.cancelQueries({ queryKey })
            const previousAttributes =
                queryClient.getQueryData<IAttribute[]>(queryKey)
            queryClient.setQueryData(
                queryKey,
                (prevAttributes: IAttribute[] | undefined) =>
                    prevAttributes?.filter(
                        (attribute: IAttribute) => attribute.id !== attributeId,
                    ),
            )
            return { previousAttributes }
        },
        onError: (_err, _attributeId, context) => {
            if (context?.previousAttributes) {
                queryClient.setQueryData(queryKey, context.previousAttributes)
            }
        },
        //refetch attributes after mutation,
        onSettled: () => queryClient.invalidateQueries({ queryKey }),
    })
}
